'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, MARKS } from '@contentful/rich-text-types';
import renderEmbeddedEntry from 'components/nodes/EmbeddedEntry';
import renderEntryHyperlink from 'components/nodes/EntryHyperlink';
import Link from 'components/shared/Link/Link';
import { DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { getCleanDocument } from 'utils/formatters';
import { cn } from 'utils/styles';
import { renderSuperscript } from 'utils/superscriptUtils';
import { useDisclosureNumberingMap } from 'contexts/DisclosureProvider';
import { BulletListStyle } from 'types/constants';
import { getUnorderedListClasses, getOrderedListClasses, renderListItem } from 'utils/bulletListHelpers';

interface RichTextListProps {
  item: Document | DocumentWithEmbeddedEntries;
  arrowStyles?: string;
  bulletListStyle?: BulletListStyle;
  checkmarkSize?: number;
  listDisplay?: 'inline' | 'block';
  listItemClasses?: string;
}

const RichTextList = ({
  item,
  arrowStyles,
  bulletListStyle,
  checkmarkSize,
  listDisplay,
  listItemClasses,
}: RichTextListProps) => {
  // Use the disclosure numbering map from context
  const disclosureNumberingMap = useDisclosureNumberingMap();
  const cleanItem = getCleanDocument(item) as Document;

  // Map Contentful bullet list style to internal bullet style
  const isInline = listDisplay === 'inline';

  return documentToReactComponents(cleanItem, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        // Avoid rendering trailing empty paragraphs
        if (!children) return null;

        if (Array.isArray(children)) {
          if (children.length === 0 || children.every(child => child === '')) {
            return null;
          }
        }
        return <p>{children}</p>;
      },
      [BLOCKS.UL_LIST]: (node, children) => {
        const bulletEnum = BulletListStyle[bulletListStyle as keyof typeof BulletListStyle];
        const ulClasses = getUnorderedListClasses(false, bulletEnum);
        const inline = isInline && 'sm:flex sm:flex-row items-center gap-6';
        return <ul className={cn(ulClasses, inline)}>{children}</ul>;
      },
      [BLOCKS.OL_LIST]: (node, children) => {
        const olClasses = getOrderedListClasses(false);
        const inline = isInline && 'sm:flex sm:flex-row items-center gap-6';
        return <ol className={cn(olClasses, inline)}>{children}</ol>;
      },
      [BLOCKS.LIST_ITEM]: (node, children) => {
        return renderListItem(children, false, arrowStyles, bulletListStyle, isInline, listItemClasses, checkmarkSize);
      },
      [INLINES.HYPERLINK]: (node, children) => {
        return (
          <Link href={node.data.uri} className="inline underline" data-testid="inline-hyperlink">
            {children}
          </Link>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: (node, children) =>
        renderEntryHyperlink(node, children, item as DocumentWithEmbeddedEntries),
      [INLINES.EMBEDDED_ENTRY]: (node, children) =>
        renderEmbeddedEntry(node, children, item as DocumentWithEmbeddedEntries, disclosureNumberingMap),
    },
    renderMark: {
      [MARKS.SUPERSCRIPT]: (children: React.ReactNode) => renderSuperscript(children),
    },
  });
};

export default RichTextList;
